import { useMelwaysLink } from '@seek/melways-react';
import type { Country, Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonLink,
  Column,
  Columns,
  ContentBlock,
  Heading,
  Stack,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import type React from 'react';

import translations from './.vocab';
import { openTour } from './IntercomTour.ts';

interface PriceCheckerProps {
  country: Country;
  language: string;
  onClick: (eventName: string) => void;
}

export const PriceCheckerPanel: React.FC<
  React.PropsWithChildren<PriceCheckerProps>
> = ({ country, language, onClick }) => {
  const { t } = useTranslations(translations);
  const localize = useMelwaysLink();

  const handleLearnMoreOnClick = () => {
    onClick('seek_contract_learn_more_about_averages_calculation');
    openTour(country);
  };

  return (
    <ContentBlock>
      <Box
        boxShadow="borderNeutralLight"
        borderRadius="xlarge"
        background="surface"
        padding="gutter"
      >
        <Stack space="gutter">
          <Heading
            level="3"
            align={{
              mobile: 'center',
              tablet: 'center',
              desktop: 'left',
            }}
          >
            {t('check ad price to see...')}
          </Heading>
          <Columns collapseBelow="desktop" space="gutter">
            <Column>
              <Stack space="small">
                <Text component="p">
                  {t('job ads on SEEK are variably...')}
                </Text>
                <Text component="p">{t('use our calculator to check...')}</Text>
              </Stack>
            </Column>
            <Column width="content">
              <ButtonLink
                variant="ghost"
                role="PriceCheckerLink"
                href={localize({
                  path: '/account/ad-price-lookup/preview',
                  language: language as Language,
                })}
                target="_blank"
                onClick={() =>
                  onClick('seek_contract_lookup_exact_ad_prices_pressed')
                }
              >
                {t('check ad price')}
              </ButtonLink>
            </Column>
          </Columns>
          <Text tone="secondary" size="small">
            {(country === 'au' || country === 'nz') && (
              <TextLinkButton
                onClick={handleLearnMoreOnClick}
                id="pricing-link"
              >
                {t('Learn more about how pricing works')}
              </TextLinkButton>
            )}
          </Text>
        </Stack>
      </Box>
    </ContentBlock>
  );
};
